import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Chaves de cache do app por usuario/empresa — removidas em todo login/logout/switch
// NAO inclui 'onboarding_v1' (preferencia local persistente — nao e dado sensivel)
const APP_STORAGE_KEYS = [
  'token',
  'dashboard_dark_mode',
  'affiliate_token',
  'affiliate_data',
  'utm_params',
  'fcod_utm',
];

const clearAppStorage = () => {
  // Remove apenas chaves do app — preserva preferences explicitas de terceiros (ex: cookies, IndexedDB)
  try {
    APP_STORAGE_KEYS.forEach(k => localStorage.removeItem(k));
    sessionStorage.clear();
  } catch (_) { /* ignore storage errors */ }
  // Limpa qualquer Authorization default deixado em axios
  try { delete axios.defaults.headers.common.Authorization; } catch (_) { /* ignore */ }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      setCompanies(response.data.companies || []);
    } catch (err) {
      // Apenas erros de auth (401/403) limpam — erros de rede/500 mantem token para retry
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        clearAppStorage();
        setUser(null);
        setCompanies([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();

    // Sincroniza auth state entre abas via storage event
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (e.newValue && e.newValue !== e.oldValue) {
          // Outra aba logou em conta diferente — forca reload completo para evitar mistura
          window.location.reload();
        } else if (!e.newValue) {
          // Outra aba deslogou — limpa estado local e redireciona
          setUser(null);
          setCompanies([]);
          // Se nao estamos em rota publica, vai pro login
          const path = window.location.pathname;
          const publicPaths = ['/', '/login', '/register', '/checkout', '/obrigado', '/afiliados', '/vendedor', '/parceiro', '/convite'];
          const isPublic = publicPaths.some(p => path === p || path.startsWith(p + '/'));
          if (!isPublic) window.location.href = '/login';
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [checkAuth]);

  const login = async (email, password) => {
    // SEMPRE limpa qualquer sessao antiga ANTES de tentar novo login.
    // Isso previne mistura de informacoes entre contas (LGPD).
    clearAppStorage();
    setUser(null);
    setCompanies([]);

    const response = await axios.post(`${API}/auth/login`, { email, password });
    const token = response.data.token;
    if (token) localStorage.setItem('token', token);
    setUser(response.data.user);
    setCompanies(response.data.user.companies || []);
    return response.data;
  };

  const register = async (data) => {
    clearAppStorage();
    setUser(null);
    setCompanies([]);

    const response = await axios.post(`${API}/auth/register`, data);
    const token = response.data.token;
    if (token) localStorage.setItem('token', token);
    setUser(response.data.user);
    setCompanies(response.data.user.companies || []);
    return response.data;
  };

  const switchCompany = async (companyId) => {
    const token = localStorage.getItem('token');
    const response = await axios.post(`${API}/auth/switch-company`,
      { company_id: companyId },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const newToken = response.data.token;
    if (newToken) localStorage.setItem('token', newToken);

    // Limpa caches por-empresa (onboarding, dark mode) ao trocar de loja,
    // mas mantem o novo token recem-setado.
    try {
      localStorage.removeItem('dashboard_dark_mode');
    } catch (_) { /* ignore */ }

    // Re-busca o user completo via /auth/me para garantir estado limpo
    // (evita spread do prev que mantinha campos da empresa anterior).
    try {
      const meRes = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${newToken || token}` }
      });
      setUser(meRes.data);
      setCompanies(meRes.data.companies || []);
    } catch (_) {
      // Fallback: usa o user retornado pelo switch (sem spread do prev)
      if (response.data.user) {
        setUser(response.data.user);
        setCompanies(response.data.user.companies || []);
      }
    }
    return response.data;
  };

  const addCompanyToList = (company) => {
    setCompanies(prev => [...prev, company]);
  };

  const logout = async () => {
    const token = localStorage.getItem('token');

    // 1) Limpa estado local IMEDIATAMENTE (evita race com navigate)
    clearAppStorage();
    setUser(null);
    setCompanies([]);

    // 2) Invalida no backend best-effort (nao bloqueia o redirect)
    if (token) {
      try {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (_) { /* ignore — limpeza local ja foi feita */ }
    }
  };

  const getToken = () => localStorage.getItem('token') || '';

  return (
    <AuthContext.Provider value={{ user, loading, companies, login, register, logout, getToken, switchCompany, addCompanyToList }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
